import { ChartData, ChartOptions, PluginOptionsByType } from "chart.js";

import Config from "./Config";

import { getColor } from "../../../../../../../../../utils/getColor";

import createExternalTooltip from "../CreateExternalTooltip";

export interface IScatterPoint {
	x: number;
	y: number;
	name: string;
	type: "employee" | "group" | "point";
	avatar?: string;
	color?: string;
	backgroundColor?: string;
	activeBorderColor?: string;
}

export interface UseScatterChartProps {
	axisLabels: {
		xAxisLabel: string;
		xAggregateLabel?: string;
		yAxisLabel: string;
		yAggregateLabel?: string;
		fontColor?: string;
		fontSize?: number;
	};
	diameter?: number;
	/** Data to plot on the chart */
	data: IScatterPoint[];
}

type ScatterTooltip = Partial<PluginOptionsByType<"scatter">["tooltip"]>;

interface ScatterChartOptions extends ChartOptions<"scatter"> {
	plugins: {
		// keep existing type definitions
		tooltip: ScatterTooltip;
		axisLabels: {
			xAxisLabel: string;
			xAggregateLabel?: string;
			yAxisLabel: string;
			yAggregateLabel?: string;
			fontColor: string;
			fontSize: number;
		};
	};
}

/**
 * A hook to return the data and options for a scatter chart
 * that uses the faxtors comparison api
 * also offers several methods to help with chart rendering
 */
const useScatterChart = (props: UseScatterChartProps) => {
	const {
		data,
		axisLabels: {
			xAxisLabel,
			xAggregateLabel,
			yAxisLabel,
			yAggregateLabel,
			fontColor = getColor("neutral", 0),
			fontSize = 14,
		},
	} = props;

	// default options for the chart
	const options: ScatterChartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		devicePixelRatio: 2,
		plugins: {
			tooltip: {
				enabled: false,
				padding: 8,
				external: (context) =>
					createExternalTooltip(context, { xAxisLabel, xAggregateLabel, yAxisLabel, yAggregateLabel }),
			},
			axisLabels: {
				xAxisLabel,
				xAggregateLabel,
				yAxisLabel,
				yAggregateLabel,
				fontColor,
				fontSize,
			},
		},
		clip: false, // keeps points from being cut off at chart edges
		// completely hide the axes, will be replaced with our custom
		scales: {
			y: {},
			x: {},
		},
	};

	// helper function to return a new options object with the scales modified
	// used to scale the x axis to -100 - 100 for eNPS
	const getScaledOptions = ({
		scales,
		padding,
	}: {
		scales: Partial<ChartOptions<"scatter">["scales"]>;
		padding?: { left?: number; bottom?: number; right?: number; top?: number };
	}) => {
		return {
			...options,
			layout: {
				padding,
			},
			scales: {
				...options.scales,
				x: {
					...options.scales?.x,
					...scales?.x,
				},
				y: {
					...options.scales?.y,
					...scales?.y,
				},
			},
		} as ChartOptions<"scatter">;
	};

	// helper function to return the data in the format that the chart expects
	const prepareDataForChart = (): ChartData<"scatter", IScatterPoint[]> => {
		let pointHitRadius = 6;
		switch (data[0]?.type) {
			case "employee":
				pointHitRadius = 12;
				break;
			case "group":
				pointHitRadius = 17.5;
				break;
		}
		return {
			...Config.DATASET_OPTIONS,
			datasets: [
				{
					pointRadius: 0,
					pointHitRadius,
					data,
				},
			],
		};
	};

	return { options, prepareDataForChart, getScaledOptions };
};

export default useScatterChart;
